import { AlertOctagon } from "lucide-react";
import { Field } from "./Field";
import { MARKET_LABEL } from "./constants";
import { formatAge, formatOdds } from "./formatters";

// Current Market card. Renders the SGO cache snapshot for a matched
// leg. Zero behavior change vs. the original inline block -- testids,
// copy, colors, ordering all preserved exactly.
export function CurrentMarketCard({ leg }) {
  const cm = leg.current_market || {};
  const market = MARKET_LABEL[leg.market] || leg.market;
  const side = leg.side ? leg.side[0].toUpperCase() + leg.side.slice(1) : "";
  const userLine = leg.user_line ?? "—";
  const marketLine = cm.line ?? "—";
  const lineDiff = cm.line_match === "different";

  return (
    <section
      data-testid="analyzer-data-check-current-market"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          Current Market
        </h3>
        <p
          className="text-xs text-slate-500 font-mono-num"
          data-testid="analyzer-data-check-timestamp"
        >
          {cm.provider || "SportsGameOdds cache"} ·{" "}
          {cm.cache_updated_at
            ? `checked ${formatAge(cm.market_data_age_minutes)}`
            : "—"}
        </p>
      </div>

      {cm.market_data_stale && (
        <p
          data-testid="analyzer-data-check-stale"
          className="mt-2 flex items-center gap-1.5 text-xs"
          style={{ color: "#92400e" }}
        >
          <AlertOctagon size={14} />
          Market data may be delayed.
        </p>
      )}

      {lineDiff && (
        <p
          data-testid="analyzer-data-check-line-warning"
          className="mt-2 text-xs"
          style={{ color: "#b45309" }}
        >
          Your slip has line{" "}
          <span className="font-mono-num">{cm.requested_line}</span>. The
          nearest cached line is{" "}
          <span className="font-mono-num">{cm.available_line}</span>.
        </p>
      )}

      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        <Field label="Player" value={leg.player_name} dataTestId="dc-player" />
        <Field
          label="Market"
          value={`${market}${side ? ` (${side})` : ""}`}
          dataTestId="dc-market"
        />
        <Field label="User Line" value={userLine} dataTestId="dc-user-line" />
        <Field
          label="Current Market Line"
          value={marketLine}
          dataTestId="dc-market-line"
        />
        <Field
          label="User Odds"
          value={formatOdds(leg.user_odds)}
          dataTestId="dc-user-odds"
        />
        <Field
          label="Best Available Price"
          value={
            cm.best_price !== null && cm.best_price !== undefined
              ? `${formatOdds(cm.best_price)} · ${cm.best_book || "—"}`
              : "—"
          }
          dataTestId="dc-best-price"
        />
        {leg.sportsbook && leg.sportsbook !== "Unknown" && (
          <Field
            label={`${leg.sportsbook} Current Price`}
            value={
              cm.user_book_current_odds !== null &&
              cm.user_book_current_odds !== undefined
                ? formatOdds(cm.user_book_current_odds)
                : "not listed"
            }
            dataTestId="dc-user-book-price"
          />
        )}
      </dl>

      {Array.isArray(cm.sportsbook_prices) && cm.sportsbook_prices.length > 0 && (
        <div className="mt-5" data-testid="analyzer-data-check-books-list">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            All Sportsbook Prices
          </p>
          <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm font-mono-num">
            {cm.sportsbook_prices.map((p) => (
              <li
                key={p.book_id}
                data-testid={`dc-book-${p.book_id}`}
                className="flex justify-between"
              >
                <span className="text-slate-600">{p.book}</span>
                <span
                  className="text-slate-900"
                  style={
                    p.book === cm.best_book
                      ? { fontWeight: 700, color: "#059669" }
                      : {}
                  }
                >
                  {formatOdds(p.odds)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p
        className="mt-6 text-xs text-slate-500 border-t border-slate-200 pt-4"
        data-testid="analyzer-data-check-footer"
      >
        Current market data pulled from the cached SportsGameOdds snapshot.
      </p>
    </section>
  );
}
